
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useState, useEffect } from "react";
import Index from "./pages/Index";
import AdminAuth from "./pages/AdminAuth";
import AdminProtected from "./components/AdminProtected";
import NotFound from "./pages/NotFound";
import { initializeLocalFallback } from "@/lib/supabase";
import { setLocalStorageItem } from "@/services/core/localStorageService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

// Create QueryClient with improved error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 1
    }
  }
});

function App() {
  const [initializing, setInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  
  useEffect(() => {
    // Chỉ khởi tạo cho các route admin
    const isAdminRoute = window.location.pathname.includes('admin');
    
    if (isAdminRoute) {
      const setupDatabase = async () => {
        try {
          setInitializing(true);
          
          // Khởi tạo dữ liệu mặc định trong localStorage để dự phòng
          setLocalStorageItem('attendees', []);
          setLocalStorageItem('adminActions', []);
          setLocalStorageItem('adminState', {
            lastActivity: new Date().toISOString(),
            activeAdmins: [],
            lastSync: new Date().toISOString()
          });
          
          // Chỉ thử kết nối đến Supabase cho các route admin
          if (isAdminRoute) {
            const { initializeSupabase } = await import('@/lib/supabase');
            
            // Thử khởi tạo Supabase với timeout
            const initPromise = initializeSupabase();
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Initialization timeout')), 10000);
            });
            
            try {
              // Race giữa khởi tạo và timeout
              await Promise.race([initPromise, timeoutPromise]) as boolean;
            } catch (error) {
              await initializeLocalFallback();
            }
          }
          
          setInitializing(false);
          setInitError(null);
        } catch (error) {
          await initializeLocalFallback();
          setInitError(error instanceof Error ? error.message : 'Lỗi không xác định khi khởi tạo ứng dụng');
          setInitializing(false);
        }
      };
      
      setupDatabase();
    }
  }, []);
  
  // Xử lý thử lại khởi tạo
  const handleRetry = () => {
    setInitError(null);
    setInitializing(true);
    
    // Thử khởi tạo lại
    const isAdminRoute = window.location.pathname.includes('admin');
    if (isAdminRoute) {
      import('@/lib/supabase')
        .then(({ initializeSupabase }) => {
          return initializeSupabase();
        })
        .then(() => {
          setInitializing(false);
        })
        .catch(error => {
          setInitError(error instanceof Error ? error.message : 'Lỗi khi thử kết nối lại');
          setInitializing(false);
        });
    } else {
      setInitializing(false);
    }
  };
  
  // Nếu đang khởi tạo, hiển thị trạng thái loading
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 border-4 border-t-blue-500 border-r-transparent border-l-transparent border-b-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-2">Đang tải ứng dụng...</h2>
          <p className="text-gray-600 text-center">Vui lòng đợi trong giây lát</p>
        </div>
      </div>
    );
  }
  
  // Nếu có lỗi khởi tạo
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Lỗi khởi tạo ứng dụng</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">{initError}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center"
                onClick={handleRetry}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Thử lại
              </Button>
            </AlertDescription>
          </Alert>
          
          <Button 
            className="w-full mt-4"
            onClick={() => {
              setInitError(null); // Tiếp tục dù có lỗi
            }}
          >
            Tiếp tục dùng ứng dụng
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/admin" element={<AdminAuth />} />
            <Route path="/admin-dashboard" element={<AdminProtected />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
